/* ============================================================
   Dathent — Admin Broadcast / mailing (/app/admin/broadcast)
   Gated server-side by requireAdmin(). Author email campaigns to
   users + pre-registrations + referrals, preview the deduped
   audience, send a test, then (when the backend flag + ESP are
   enabled) send to all. Mass send is flag-gated on the server, so
   this surface is safe to ship dark.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, DathentAPIError } from '../services/api/client.js';

const SOURCE_LABELS = {
  users: 'Users (active accounts)',
  pre_registrations: 'Pre-registrations (opted-in)',
  referral_signups: 'Referral signups',
};
const ALL_SOURCES = ['users', 'pre_registrations', 'referral_signups'];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

const card = {
  background: 'var(--surface, #16161a)',
  border: '1px solid var(--border, #2a2a30)',
  borderRadius: 14,
  padding: 20,
};
const label = { display: 'block', fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '12px 0 4px' };
const input = {
  width: '100%', boxSizing: 'border-box', background: 'var(--surface-2, #0f0f12)',
  border: '1px solid var(--border, #2a2a30)', borderRadius: 10, color: 'var(--text, #ececf0)',
  padding: '10px 12px', fontSize: 14, fontFamily: 'inherit',
};
const btn = {
  background: 'var(--gold, #caa24a)', color: '#0b0b0c', border: 0, borderRadius: 10,
  padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const btnGhost = {
  background: 'transparent', color: 'var(--text, #ececf0)', border: '1px solid var(--border, #2a2a30)',
  borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
};

function StatusPill({ status }) {
  const color = {
    draft: '#9a9aa2', sending: '#caa24a', sent: '#5ec27a', failed: '#e0685f', canceled: '#9a9aa2',
  }[status] || '#9a9aa2';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color,
      border: `1px solid ${color}55`, borderRadius: 999, padding: '2px 10px',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color }} /> {status}
    </span>
  );
}

function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 10, fontSize: 13,
      background: '#e0685f18', border: '1px solid #e0685f55', color: '#e8a59f',
    }}>{error}</div>
  );
}

function errMsg(e) {
  if (e instanceof DathentAPIError) return e.message || 'Request failed';
  return e?.message || 'Request failed';
}

// ─── create form ──────────────────────────────────────────────────────────────

function CreateForm({ onCreated }) {
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sources, setSources] = useState([...ALL_SOURCES]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggleSource = (s) =>
    setSources((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const submit = async () => {
    setError(null);
    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!bodyText.trim()) { setError('Message body is required.'); return; }
    if (sources.length === 0) { setError('Pick at least one audience source.'); return; }
    setBusy(true);
    try {
      const { campaign } = await api.adminBroadcast.createCampaign({
        channel: 'email',
        subject: subject.trim(),
        fromName: fromName.trim() || undefined,
        replyTo: replyTo.trim() || undefined,
        bodyText,
        bodyHtml: bodyHtml.trim() || undefined,
        audience: { sources },
      });
      onCreated(campaign);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={card}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>New email campaign</h2>
      <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>
        Creates a draft. Nothing is sent until you choose to.
      </p>

      <label style={label}>Subject</label>
      <input style={input} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject line" maxLength={300} />

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={label}>From name (optional)</label>
          <input style={input} value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Dathent" maxLength={120} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={label}>Reply-to (optional)</label>
          <input style={input} value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="info@dathent.com" maxLength={320} />
        </div>
      </div>

      <label style={label}>Message (plain text)</label>
      <textarea style={{ ...input, minHeight: 140, resize: 'vertical' }} value={bodyText} onChange={(e) => setBodyText(e.target.value)} placeholder="Write your message…" />

      <label style={label}>HTML body (optional — overrides the plain-text rendering in HTML clients)</label>
      <textarea style={{ ...input, minHeight: 100, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} placeholder="<p>Optional HTML…</p>" />

      <label style={label}>Audience</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ALL_SOURCES.map((s) => (
          <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={sources.includes(s)} onChange={() => toggleSource(s)} />
            {SOURCE_LABELS[s]}
          </label>
        ))}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)', marginTop: 8 }}>
        Unsubscribed addresses are always excluded. An unsubscribe link is added to every email automatically.
      </p>

      <ErrorNote error={error} />
      <div style={{ marginTop: 16 }}>
        <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </div>
  );
}

// ─── campaign detail ────────────────────────────────────────────────────────────

function CampaignDetail({ id, onChanged }) {
  const [campaign, setCampaign] = useState(null);
  const [byStatus, setByStatus] = useState({});
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState(null);
  const [confirmCount, setConfirmCount] = useState('');
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.adminBroadcast.getCampaign(id);
      setCampaign(res.campaign);
      setByStatus(res.recipientsByStatus || {});
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); setAudience(null); setTestMsg(null); setProgress(null); setConfirmCount(''); }, [id, load]);

  const preview = async () => {
    setError(null);
    try {
      const res = await api.adminBroadcast.previewAudience(id);
      setAudience(res);
    } catch (e) { setError(errMsg(e)); }
  };

  const sendTest = async () => {
    setTestMsg(null); setError(null);
    if (!testTo.trim()) { setError('Enter a test recipient email.'); return; }
    setBusy(true);
    try {
      const res = await api.adminBroadcast.sendTest(id, testTo.trim());
      setTestMsg(res.sent ? `Test sent to ${res.testSentTo}.` : `Test not sent: ${res.error || 'unknown error'}`);
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };

  const sendAll = async () => {
    setError(null); setProgress(null);
    setBusy(true);
    try {
      let res = await api.adminBroadcast.send(id);
      let acc = { sent: res.sent || 0, failed: res.failed || 0, total: res.total || 0, remaining: res.remaining ?? 0 };
      setProgress(acc);
      // Drain remaining batches.
      let guard = 0;
      while (!res.done && (res.remaining ?? 0) > 0 && guard < 1000) {
        guard += 1;
        res = await api.adminBroadcast.dispatch(id);
        acc = { sent: acc.sent + (res.sent || 0), failed: acc.failed + (res.failed || 0), total: acc.total, remaining: res.remaining ?? 0 };
        setProgress({ ...acc });
      }
      await load();
      onChanged?.();
    } catch (e) {
      setError(errMsg(e));
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={card}>Loading…</div>;
  if (!campaign) return <div style={card}><ErrorNote error={error || 'Not found'} /></div>;

  const canSend = campaign.status === 'draft' || campaign.status === 'failed';
  const audienceTotal = audience?.total ?? null;
  const confirmOk = audienceTotal != null && String(audienceTotal) === confirmCount.trim();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>{campaign.subject || '(no subject)'}</h2>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusPill status={campaign.status} />
              <span style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>{campaign.channel}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)' }}>· created {fmtDate(campaign.createdAt)}</span>
            </div>
          </div>
          <button style={btnGhost} onClick={load}>Refresh</button>
        </div>

        {(campaign.status === 'sending' || campaign.status === 'sent') && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted, #9a9aa2)' }}>
            {campaign.sentCount} sent · {campaign.failedCount} failed · {campaign.totalRecipients} total
            {byStatus.pending ? ` · ${byStatus.pending} pending` : ''}
          </div>
        )}

        <div style={{ marginTop: 14, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, color: 'var(--text, #d8d8de)', borderTop: '1px solid var(--border,#2a2a30)', paddingTop: 14 }}>
          {campaign.bodyText}
        </div>
      </div>

      {/* Audience preview */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Audience</h3>
          <button style={btnGhost} onClick={preview}>Preview audience</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '8px 0 0' }}>
          Sources: {(campaign.audience?.sources || []).map((s) => SOURCE_LABELS[s] || s).join(', ') || '—'}
        </p>
        {audience && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{audience.total.toLocaleString()}</div>
            <div style={{ color: 'var(--text-muted, #9a9aa2)' }}>
              recipients (deduped, minus unsubscribed) ·{' '}
              {Object.entries(audience.bySource || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
            </div>
            {Array.isArray(audience.sample) && audience.sample.length > 0 && (
              <div style={{ marginTop: 8, color: 'var(--text-muted, #9a9aa2)', fontSize: 12 }}>
                e.g. {audience.sample.map((r) => r.email).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Test send */}
      <div style={card}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Send a test</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '0 0 10px' }}>
          Sends one email to the address below. Works regardless of the mass-send flag.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...input, flex: 1 }} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
          <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={sendTest}>Send test</button>
        </div>
        {testMsg && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text, #d8d8de)' }}>{testMsg}</div>}
      </div>

      {/* Send to all */}
      {canSend && (
        <div style={{ ...card, border: '1px solid #caa24a55' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Send to everyone</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted, #9a9aa2)', margin: '0 0 10px' }}>
            Preview the audience first, then type the recipient count to confirm. Mass send must be enabled on the
            server (BROADCAST_LIVE_SEND=1) and an email provider configured — otherwise this returns an error and nothing is sent.
          </p>
          {audienceTotal == null ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted, #9a9aa2)' }}>Run “Preview audience” above to enable sending.</p>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input style={{ ...input, width: 160 }} value={confirmCount} onChange={(e) => setConfirmCount(e.target.value)} placeholder={`Type ${audienceTotal}`} />
              <button
                style={{ ...btn, background: '#e0685f', color: '#fff', opacity: confirmOk && !busy ? 1 : 0.5 }}
                disabled={!confirmOk || busy}
                onClick={sendAll}
              >
                {busy ? 'Sending…' : `Send to ${audienceTotal}`}
              </button>
            </div>
          )}
          {progress && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              Sent {progress.sent} · failed {progress.failed} · remaining {progress.remaining}
            </div>
          )}
        </div>
      )}

      <ErrorNote error={error} />
    </div>
  );
}

// ─── screen shell ───────────────────────────────────────────────────────────────

export function AdminBroadcast() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('list'); // 'list' | 'create'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { campaigns: rows } = await api.adminBroadcast.listCampaigns();
      setCampaigns(rows || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const onCreated = (campaign) => {
    setMode('list');
    setSelectedId(campaign.id);
    loadList();
  };

  const selected = useMemo(() => campaigns.find((c) => c.id === selectedId) || null, [campaigns, selectedId]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '8px 4px 64px' }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--gold, #caa24a)' }}>Admin · internal</div>
        <h1 style={{ margin: '4px 0 2px', fontSize: 26 }}>Broadcast</h1>
        <p style={{ margin: 0, color: 'var(--text-muted, #9a9aa2)', fontSize: 14 }}>
          Email your users, pre-registrations and referral signups. Authoring &amp; test sends are always available;
          mass send is gated by a server flag and an email provider.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: 20, alignItems: 'start' }}>
        {/* Left: list */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 15 }}>Campaigns</h2>
            <button style={btn} onClick={() => { setMode('create'); setSelectedId(null); }}>+ New</button>
          </div>
          {loading ? (
            <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>Loading…</p>
          ) : campaigns.length === 0 ? (
            <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 13 }}>No campaigns yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {campaigns.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedId(c.id); setMode('list'); }}
                  style={{
                    textAlign: 'left', background: selectedId === c.id ? 'var(--surface-2, #0f0f12)' : 'transparent',
                    border: '1px solid var(--border, #2a2a30)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                    color: 'var(--text, #ececf0)',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.subject || '(no subject)'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <StatusPill status={c.status} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted, #9a9aa2)' }}>{fmtDate(c.createdAt)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <ErrorNote error={error} />
        </div>

        {/* Right: editor / detail */}
        <div>
          {mode === 'create' ? (
            <CreateForm onCreated={onCreated} />
          ) : selected ? (
            <CampaignDetail id={selected.id} onChanged={loadList} />
          ) : (
            <div style={{ ...card, color: 'var(--text-muted, #9a9aa2)' }}>
              Select a campaign on the left, or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBroadcast;
